import Form from 'antd/lib/form';
import * as React from 'react';
import {
    AllAppStoreArtifactUploadRules,
    AppPlatform,
    AppStoreArtifactUploadDestinationType,
    AppStoreArtifactUploadRule,
    AppStoreArtifactUploadWorkflowSettings,
    AppStoreType,
    Automation,
    AutomationAutoUploadBinaryArtifactToAppStore,
    AutomationAutoUploadDsymsToStabilityMonitoring,
    AutomationType,
    BAAutomationAutoUploadBinaryArtifactToAppStore,
    BaseCIIntegrationData,
    DsymUploadDestinationOption,
    disallowedTokensForForAndroidTrackReleaseName,
    IntegrationType,
    PatternToken,
    RequireAtLeastOneOfTokensForAndroidTrackReleaseName,
    WorkflowType,
} from '../../types';
import { DeleteOutlined, InfoCircleOutlined, PlusOutlined } from '@ant-design/icons';
import Text from 'antd/lib/typography/Text';
import Space from 'antd/lib/space';
import { validatePattern } from '../../helpers';
import { displayTextForAppStoreUploadRule, placeholderForFileTypePlatform } from '../../helpers/displayHelpers';
import { FieldType, FormFieldData, validateFileNameForAppStoreType } from '../../helpers/formHelpers';
import {
    AppStoreArtifactUploadDestinationTypeOption,
    AppStoreArtifactUploadWorkflowOption,
    DsymsUploadWorkflowOption,
} from '../../typescriptHelpers';
import { globalMessage } from '../../utils/globalMessage';
import Button from 'antd/lib/button';
import Input from 'antd/lib/input';
import Popover from 'antd/lib/popover';
import Select from 'antd/lib/select';
import { antGrey, runwayColors } from '../styles/globalStyles';
import { getIntegrationTypeForInstallationId } from '../../helpers/integrationHelpers';
import { PatternTokenHelperTextComponent } from '../appSettings/PatternTokenHelperTextComponent';
import Tooltip from 'antd/lib/tooltip';

const UPLOAD_BINARY_AVAILABLE_TOKENS = [
    PatternToken.Version,
    PatternToken.VersionConcise,
    PatternToken.ReleaseName,
    PatternToken.BuildCI,
    PatternToken.ReleasePilot,
    PatternToken.ReleaseBranchName,
    PatternToken.WorkingBranchName,
    PatternToken.Wildcard,
];

export function generateAutoUploadBinaryArtifactToAppStoreFields(
    baseField: FormFieldData,
    automation: Automation,
    platform: AppPlatform,
    appStoreType: AppStoreType,
    destinationOptions: AppStoreArtifactUploadDestinationTypeOption[],
    buildSystemData: BaseCIIntegrationData,
    isCustomCI: boolean,
) {
    const baseFieldWithConditionsValidation = {
        ...baseField,
        rules: [
            (form) => {
                return {
                    validator: (rule, value) => {
                        if (!value) {
                            return Promise.resolve();
                        }

                        const uploadWorkflowSettings: AppStoreArtifactUploadWorkflowSettings[] =
                            form.getFieldValue('uploadWorkflowSettings') ?? [];
                        if (uploadWorkflowSettings.length === 0) {
                            const message = 'At least one rule is required to enable this automation';
                            globalMessage.error(message);
                            return Promise.reject(message);
                        } else {
                            const stagingWorkflowSettings = uploadWorkflowSettings.filter((setting) => {
                                return setting.workflowType == WorkflowType.Staging;
                            });
                            const prodWorkflowSettings = uploadWorkflowSettings.filter((setting) => {
                                return setting.workflowType == WorkflowType.Production;
                            });
                            if (stagingWorkflowSettings.length > 2 && prodWorkflowSettings.length > 1) {
                                const message =
                                    'A maximum of one release rule and two release candidate rules are allowed';
                                globalMessage.error(message);
                                return Promise.reject(message);
                            } else if (stagingWorkflowSettings.length > 2) {
                                const message = 'A maximum of two release candidate rules are allowed';
                                globalMessage.error(message);
                                return Promise.reject(message);
                            } else if (prodWorkflowSettings.length > 1) {
                                const message = 'A maximum of one release rule is allowed';
                                globalMessage.error(message);
                                return Promise.reject(message);
                            } else {
                                // Have the correct number of rules configured, checking for duplicates
                                // TODO:RUN-10317 duplicates checking..
                                if (stagingWorkflowSettings.length === 2) {
                                    if (
                                        stagingWorkflowSettings[0].destinationInstallationId ==
                                            stagingWorkflowSettings[1].destinationInstallationId &&
                                        stagingWorkflowSettings[0].workflowType ==
                                            stagingWorkflowSettings[1].workflowType &&
                                        stagingWorkflowSettings[0].fileNames.filter(
                                            (fileName) => stagingWorkflowSettings[1].fileNames.indexOf(fileName) < 0,
                                        ).length === 0
                                    ) {
                                        const message = 'Each rule must be unique';
                                        globalMessage.error(message);
                                        return Promise.reject(message);
                                    }
                                }
                                return Promise.resolve();
                            }
                        }
                    },
                };
            },
        ],
    };

    const appStoreUploadAutomation = automation as
        | AutomationAutoUploadBinaryArtifactToAppStore
        | BAAutomationAutoUploadBinaryArtifactToAppStore;

    const uploadRulesOptions = AllAppStoreArtifactUploadRules.map((rule) => {
        const displayText = displayTextForAppStoreUploadRule(rule);

        return {
            value: rule,
            label: displayText,
        };
    });

    return [
        baseFieldWithConditionsValidation,
        {
            label: 'Upload behavior for multiple available builds',
            name: 'uploadRule',
            fieldType: FieldType.Custom,
            showDivider: true,
            initialValue: appStoreUploadAutomation.data?.uploadRule ?? AppStoreArtifactUploadRule.UploadNewestBuildOnly,
            data: uploadRulesOptions,
            rules: [{ required: true, message: 'Upload behavior is required' }],
            renderField: () => {
                return (
                    <Select
                        placeholder="Select an upload behavior"
                        style={{
                            width: '100%',
                        }}
                    >
                        {uploadRulesOptions.map((option) => {
                            return (
                                <Select.Option value={option.value} key={option.value}>
                                    {option.label}
                                </Select.Option>
                            );
                        })}
                    </Select>
                );
            },
        },
        buildArtifactUploadWorkflowSettingsField(
            automation,
            platform,
            appStoreType,
            destinationOptions,
            buildSystemData,
            isCustomCI,
        ),
    ];
}

export function buildArtifactUploadWorkflowSettingsField(
    automation: Automation,
    platform: AppPlatform,
    appStoreType: AppStoreType,
    destinationOptions: AppStoreArtifactUploadDestinationTypeOption[],
    buildSystemData: BaseCIIntegrationData,
    isCustomCI: boolean,
): FormFieldData {
    const appStoreUploadAutomation = automation as
        | AutomationAutoUploadBinaryArtifactToAppStore
        | BAAutomationAutoUploadBinaryArtifactToAppStore;
    const isBucketAutomation = automation.type === AutomationType.BAAutoUploadBinaryArtifactToAppStore;

    return {
        label: null,
        name: 'uploadWorkflowSettings',
        fieldType: FieldType.List,
        showDivider: true,
        initialValue: appStoreUploadAutomation.data?.uploadWorkflowSettings,
        nestedListFields: [
            {
                fieldType: FieldType.Custom,
                style: { margin: 0 },
                renderField: (data) => {
                    return renderUploadWorkflowSettingsField(
                        data,
                        platform,
                        appStoreType,
                        destinationOptions,
                        isBucketAutomation,
                        isCustomCI,
                        buildSystemData,
                    );
                },
            },
        ],
        listChildrenWrapper: (children, { add }) => {
            const buttonText = (children as React.ReactNode[]).length === 0 ? 'Add rule' : 'Add another rule';
            return (
                <Space
                    direction="vertical"
                    style={{
                        width: '100%',
                    }}
                    size={16}
                >
                    {children}
                    <Form.Item shouldUpdate noStyle>
                        {() => {
                            return (
                                <>
                                    <Button
                                        disabled={false}
                                        icon={<PlusOutlined style={{ color: runwayColors.primary }} />}
                                        onClick={() => add()}
                                    >
                                        {buttonText}
                                    </Button>
                                </>
                            );
                        }}
                    </Form.Item>
                </Space>
            );
        },
    } as FormFieldData;
}

const renderUploadWorkflowSettingsField: (
    data: any,
    platform: AppPlatform,
    appStoreType: AppStoreType,
    destinationOptions: AppStoreArtifactUploadDestinationTypeOption[],
    isBucketAutomation,
    isCustomCI,
    buildSystemData?: BaseCIIntegrationData,
) => React.ReactNode = (
    { index, listOperations: { remove }, listFieldData },
    platform,
    appStoreType,
    destinationOptions,
    isBucketAutomation,
    isCustomCI,
    buildSystemData,
) => {
    const needsWorkflowInfo = buildSystemData && !isBucketAutomation && !isCustomCI;
    const workflowOptions: AppStoreArtifactUploadWorkflowOption[] = [];

    if (needsWorkflowInfo) {
        if (buildSystemData.workflowName) {
            const stagingWorkflow: AppStoreArtifactUploadWorkflowOption = {
                id: WorkflowType.Staging,
                workflowDescription: 'Release candidate workflow',
                workflowName: buildSystemData.workflowName,
            };
            workflowOptions.push(stagingWorkflow);
        }
        if (buildSystemData.prodWorkflowName) {
            const prodWorkflow: AppStoreArtifactUploadWorkflowOption = {
                id: WorkflowType.Production,
                workflowDescription: 'Release workflow',
                workflowName: buildSystemData.prodWorkflowName,
            };
            workflowOptions.push(prodWorkflow);
        }
    }

    const appStoreTypesSupportingMultiUploads = [AppStoreType.Google];
    const supportsMultiUploads = appStoreTypesSupportingMultiUploads.includes(appStoreType);

    const filePlaceholder = placeholderForFileTypePlatform(platform, appStoreType);

    return (
        <Form.Item
            shouldUpdate
            noStyle
            style={{
                width: '100%',
            }}
        >
            {(form) => {
                const selectedDestinationInstallationId =
                    form.getFieldValue('uploadWorkflowSettings')?.[index]?.destinationInstallationId ?? '';

                // Firebase App Distribution has no tracks, so its upload ignores both track fields.
                const isFirebaseDestination =
                    getIntegrationTypeForInstallationId(selectedDestinationInstallationId) ===
                    IntegrationType.FirebaseBeta;

                return (
                    <>
                        <Space
                            direction="vertical"
                            style={{
                                width: '100%',
                                backgroundColor: antGrey.grey2,
                                borderRadius: 8,
                                padding: '20px 30px',
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: 16,
                                }}
                            >
                                <Text strong style={{ fontSize: 16 }}>
                                    Rule {index + 1}
                                </Text>
                                <Button
                                    icon={<DeleteOutlined style={{ color: runwayColors.statusRed }} />}
                                    danger
                                    type="link"
                                    onClick={() => remove(index)}
                                />
                            </div>
                            {needsWorkflowInfo && (
                                <Form.Item
                                    label="Workflow"
                                    style={{
                                        width: '100%',
                                    }}
                                    {...listFieldData}
                                    rules={[
                                        {
                                            required: true,
                                            message: 'Please select a workflow',
                                            type: 'string',
                                        },
                                    ]}
                                    required
                                    name={[listFieldData.name, 'workflowType']}
                                >
                                    <Select
                                        placeholder="Select a workflow"
                                        style={{
                                            width: '100%',
                                        }}
                                    >
                                        {workflowOptions.map((option) => {
                                            return (
                                                <Select.Option value={option.id} key={option.id}>
                                                    {option.workflowDescription}{' '}
                                                    <Text style={{ color: antGrey.grey7 }}>
                                                        — {option.workflowName}
                                                    </Text>
                                                </Select.Option>
                                            );
                                        })}
                                    </Select>
                                </Form.Item>
                            )}
                            {!isBucketAutomation && (
                                <Form.Item
                                    label="File name"
                                    {...listFieldData}
                                    extra={
                                        <Space>
                                            {' '}
                                            <Text style={{ color: antGrey.grey7 }}>
                                                {!supportsMultiUploads ?
                                                    'The file name of the artifact that will be uploaded to the target destination for this rule.'
                                                :   'The file name(s) of the artifact(s) that will be uploaded to the target destination for this rule.'
                                                }
                                                Tokenized patterns and wildcards are supported.
                                            </Text>{' '}
                                            <Popover
                                                content={
                                                    <PatternTokenHelperTextComponent
                                                        availableTokens={UPLOAD_BINARY_AVAILABLE_TOKENS}
                                                    />
                                                }
                                            >
                                                <InfoCircleOutlined style={{ color: antGrey.grey7 }} />
                                            </Popover>
                                        </Space>
                                    }
                                    rules={
                                        !supportsMultiUploads ?
                                            [
                                                {
                                                    required: true,
                                                },
                                                {
                                                    required: true,
                                                    type: 'array',
                                                    validator: (_, fileNames) => {
                                                        if (!Array.isArray(fileNames)) {
                                                            return Promise.reject('Must provide one valid filename');
                                                        }
                                                        if (fileNames.length > 1) {
                                                            return Promise.reject('Only one filename allowed.');
                                                        }
                                                        for (const fileName of fileNames) {
                                                            const [isAcceptableFileName, msg] =
                                                                validateFileNameForAppStoreType(fileName, appStoreType);
                                                            if (isAcceptableFileName) {
                                                                return Promise.resolve();
                                                            } else {
                                                                return Promise.reject(msg);
                                                            }
                                                        }
                                                        return Promise.reject('Must provide one valid filename');
                                                    },
                                                },
                                            ]
                                        :   [
                                                {
                                                    required: true,
                                                },
                                                {
                                                    required: true,
                                                    validator: (_, fileNames) => {
                                                        for (const fileName of fileNames) {
                                                            const [isAcceptableFileName, msg] =
                                                                validateFileNameForAppStoreType(fileName, appStoreType);
                                                            if (isAcceptableFileName) {
                                                                return Promise.resolve();
                                                            } else {
                                                                return Promise.reject(msg);
                                                            }
                                                        }
                                                        return Promise.reject(
                                                            'Must provide at least one valid filename',
                                                        );
                                                    },
                                                },
                                            ]
                                    }
                                    name={[listFieldData.name, 'fileNames']}
                                >
                                    <Select
                                        mode="tags"
                                        allowClear
                                        tokenSeparators={[',', '\n']}
                                        dropdownStyle={{ display: 'none' }}
                                        style={{ width: '100%' }}
                                        placeholder={filePlaceholder}
                                    />
                                </Form.Item>
                            )}
                            <Form.Item
                                label="Destination"
                                required
                                shouldUpdate
                                {...listFieldData}
                                rules={[
                                    {
                                        required: true,
                                        message: 'Please select a destination',
                                        type: 'string',
                                    },
                                ]}
                                name={[listFieldData.name, 'destinationInstallationId']}
                                extra={`The target destination for the artifact defined by this rule. Destination options are determined by your app's app store and beta integrations.${
                                    appStoreType === AppStoreType.Amazon ?
                                        `Runway will replace the APK with the highest version code in the current
                                           edit in the Amazon Developer Portal with the defined artifact.`
                                    : appStoreType === AppStoreType.Huawei ?
                                        'Uploading a build will also select it for submission in Huawei AppGallery.'
                                    : appStoreType === AppStoreType.Samsung ?
                                        'Uploading a build will also select it for submission in the Samsung Galaxy Store.'
                                    :   ''
                                }`}
                            >
                                <Select
                                    placeholder="Select a destination"
                                    style={{
                                        flex: 1,
                                        width: '100%',
                                    }}
                                >
                                    {destinationOptions.map((option) => {
                                        let disabled = false;
                                        if (
                                            form.getFieldValue('uploadWorkflowSettings')?.[index]?.workflowType ==
                                                WorkflowType.Production &&
                                            option.type == AppStoreArtifactUploadDestinationType.DestinationBetaTesting
                                        ) {
                                            disabled = true;
                                        }
                                        const destinationAlias = option.alias?.trim();

                                        return (
                                            <Select.Option value={option.id} key={option.id} disabled={disabled}>
                                                {option.name}
                                                {destinationAlias && (
                                                    <Text style={{ color: antGrey.grey7 }}> {destinationAlias}</Text>
                                                )}
                                                <Text style={{ color: antGrey.grey7 }}> — </Text>
                                                {option.identifier}
                                            </Select.Option>
                                        );
                                    })}
                                </Select>
                            </Form.Item>
                            {appStoreType === AppStoreType.Google && (
                                <Form.Item
                                    label="Track"
                                    shouldUpdate
                                    {...listFieldData}
                                    name={[listFieldData.name, 'trackName']}
                                    extra="Builds will be added to the track and released to all users immediately."
                                    rules={[
                                        (form) => {
                                            return {
                                                validator: (_, selectedTrack) => {
                                                    const selectedDestinationInstallId =
                                                        form.getFieldValue('uploadWorkflowSettings')?.[index]
                                                            ?.destinationInstallationId;
                                                    const selectedDestination = destinationOptions.find(
                                                        (option) =>
                                                            !!selectedDestinationInstallId &&
                                                            option.id === selectedDestinationInstallId,
                                                    );
                                                    if (
                                                        selectedDestination &&
                                                        selectedDestination.type ===
                                                            AppStoreArtifactUploadDestinationType.DestinationAppStore &&
                                                        selectedTrack === 'production'
                                                    ) {
                                                        return Promise.reject(
                                                            `The production track cannot be selected with the ${selectedDestination.name} destination.`,
                                                        );
                                                    }

                                                    return Promise.resolve();
                                                },
                                            };
                                        },
                                    ]}
                                >
                                    <Select
                                        placeholder="Select a track"
                                        style={{
                                            width: '100%',
                                        }}
                                        allowClear
                                        disabled={isFirebaseDestination}
                                    >
                                        {destinationOptions.map((option) => {
                                            if (
                                                form.getFieldValue('uploadWorkflowSettings')?.[index]
                                                    ?.destinationInstallationId === option.id
                                            ) {
                                                const trackNames = option.availableTracks;
                                                return (
                                                    trackNames &&
                                                    trackNames.map((track) => {
                                                        return (
                                                            <Select.Option value={track} key={track}>
                                                                {track}
                                                            </Select.Option>
                                                        );
                                                    })
                                                );
                                            }
                                            return null;
                                        })}
                                    </Select>
                                </Form.Item>
                            )}
                            {appStoreType === AppStoreType.Google && (
                                <Form.Item
                                    label="Create new track release with name"
                                    shouldUpdate
                                    {...listFieldData}
                                    name={[listFieldData.name, 'createNewTrackReleaseWithPatternedName']}
                                    extra="Optionally create a new track release per upload using the supplied naming pattern. If left blank, Runway will use an existing track release matching the version in question."
                                    rules={[
                                        {
                                            validator: (rule, value) => {
                                                if (!value) {
                                                    return Promise.resolve();
                                                }

                                                const pattern = value as string;

                                                return validatePattern(
                                                    pattern,
                                                    'Release name',
                                                    RequireAtLeastOneOfTokensForAndroidTrackReleaseName,
                                                    disallowedTokensForForAndroidTrackReleaseName(),
                                                );
                                            },
                                        },
                                    ]}
                                >
                                    <Input
                                        placeholder="e.g. {version}.{appStoreBuildNumber}"
                                        autoComplete="off"
                                        allowClear
                                        disabled={isFirebaseDestination}
                                    />
                                </Form.Item>
                            )}
                        </Space>
                    </>
                );
            }}
        </Form.Item>
    );
};

export function buildDsymsUploadWorkflowSettingsField(
    automation: Automation,
    buildSystemData: BaseCIIntegrationData,
    destinationOptions: DsymUploadDestinationOption[],
): FormFieldData {
    const workflowOptions: DsymsUploadWorkflowOption[] = [];
    if (buildSystemData) {
        if (buildSystemData.workflowName) {
            const stagingWorkflow: DsymsUploadWorkflowOption = {
                id: WorkflowType.Staging,
                workflowDescription: 'Release candidate workflow',
                workflowName: buildSystemData.workflowName,
            };
            workflowOptions.push(stagingWorkflow);
        }
        if (buildSystemData.prodWorkflowName) {
            const prodWorkflow: DsymsUploadWorkflowOption = {
                id: WorkflowType.Production,
                workflowDescription: 'Release workflow',
                workflowName: buildSystemData.prodWorkflowName,
            };
            workflowOptions.push(prodWorkflow);
        }
    }

    const dsymsUploadAutomation = automation as AutomationAutoUploadDsymsToStabilityMonitoring;
    const savedSettings = dsymsUploadAutomation.data?.uploadWorkflowSettings;
    const defaultStabilityMonitoringInstallationIds = destinationOptions.map((option) => option.installationId);
    const uploadSettingsFields: {
        workflowType: WorkflowType | null;
        fileName: string;
        stabilityMonitoringInstallationIds: string[];
    }[] = [];
    if (savedSettings) {
        savedSettings.forEach((setting) => {
            uploadSettingsFields.push({
                workflowType: setting.workflowType,
                fileName: setting.fileNames?.[0] ?? `{*}.dSYM.zip`,
                stabilityMonitoringInstallationIds:
                    setting.stabilityMonitoringInstallationIds ?? defaultStabilityMonitoringInstallationIds,
            });
        });
    } else {
        uploadSettingsFields.push({
            workflowType: null,
            fileName: `{*}.dSYM.zip`,
            stabilityMonitoringInstallationIds: defaultStabilityMonitoringInstallationIds,
        });
    }

    const availableTokens: PatternToken[] = [
        PatternToken.Version,
        PatternToken.VersionConcise,
        PatternToken.ReleaseName,
        PatternToken.BuildCI,
        PatternToken.ReleasePilot,
        PatternToken.ReleaseBranchName,
        PatternToken.WorkingBranchName,
        PatternToken.Wildcard,
    ];

    return {
        label: null,
        name: 'uploadWorkflowSettings',
        fieldType: FieldType.List,
        showDivider: true,
        initialValue: uploadSettingsFields,
        nestedListFields: [
            {
                fieldType: FieldType.Custom,
                style: { margin: 0 },
                renderField: ({ index, listOperations: { remove }, listFieldData }) => {
                    return (
                        <Form.Item
                            shouldUpdate
                            noStyle
                            style={{
                                width: '100%',
                            }}
                        >
                            {() => {
                                return (
                                    <>
                                        <Space
                                            direction="vertical"
                                            size={14}
                                            style={{
                                                width: '100%',
                                                backgroundColor: antGrey.grey2,
                                                borderRadius: 8,
                                                padding: '20px 30px',
                                            }}
                                        >
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <Text>Rule {index + 1}</Text>
                                                <Button
                                                    icon={<DeleteOutlined style={{ color: runwayColors.statusRed }} />}
                                                    danger
                                                    type="link"
                                                    onClick={() => remove(index)}
                                                />
                                            </div>
                                            <Space
                                                direction="vertical"
                                                size={20}
                                                style={{
                                                    width: '100%',
                                                    paddingTop: 8,
                                                }}
                                            >
                                                <Space align="center">
                                                    <Text
                                                        style={{
                                                            fontWeight: 600,
                                                            color: antGrey.grey8,
                                                        }}
                                                    >
                                                        Workflow
                                                    </Text>
                                                    <Tooltip title="The type of CI/CD workflow whose dSYM file the setting will apply to">
                                                        <InfoCircleOutlined style={{ color: antGrey.grey6 }} />
                                                    </Tooltip>
                                                </Space>
                                                <div
                                                    style={{
                                                        width: '100%',
                                                        display: 'flex',
                                                        gap: 8,
                                                        alignItems: 'center',
                                                        marginTop: -4,
                                                    }}
                                                >
                                                    <Form.Item
                                                        noStyle
                                                        style={{
                                                            width: '100%',
                                                        }}
                                                        {...listFieldData}
                                                        rules={[
                                                            {
                                                                required: true,
                                                                message: 'Please select a workflow',
                                                                type: 'string',
                                                            },
                                                        ]}
                                                        name={[listFieldData.name, 'workflowType']}
                                                    >
                                                        <Select
                                                            placeholder="Select a workflow"
                                                            style={{
                                                                width: '100%',
                                                            }}
                                                        >
                                                            {workflowOptions.map((option) => {
                                                                return (
                                                                    <Select.Option value={option.id} key={option.id}>
                                                                        {option.workflowDescription}{' '}
                                                                        <Text style={{ color: antGrey.grey7 }}>
                                                                            — {option.workflowName}
                                                                        </Text>
                                                                    </Select.Option>
                                                                );
                                                            })}
                                                        </Select>
                                                    </Form.Item>
                                                </div>
                                            </Space>

                                            <Space
                                                direction="vertical"
                                                size={14}
                                                style={{
                                                    width: '100%',
                                                    paddingTop: 8,
                                                }}
                                            >
                                                <Space align="center">
                                                    <Text
                                                        style={{
                                                            fontWeight: 600,
                                                            color: antGrey.grey8,
                                                        }}
                                                    >
                                                        File name
                                                    </Text>
                                                    <Tooltip
                                                        title={
                                                            'The name of the dSYM zip file that will be uploaded to the target destination for this setting. The default value of {*}.dSYM.zip will be used if no file name is specified.'
                                                        }
                                                    >
                                                        <InfoCircleOutlined style={{ color: antGrey.grey6 }} />
                                                    </Tooltip>
                                                </Space>
                                                <div
                                                    style={{
                                                        width: '100%',
                                                        display: 'flex',
                                                        gap: 8,
                                                        alignItems: 'center',
                                                        marginTop: -4,
                                                    }}
                                                >
                                                    <Form.Item
                                                        noStyle
                                                        style={{
                                                            width: '100%',
                                                        }}
                                                        {...listFieldData}
                                                        rules={[
                                                            {
                                                                type: 'string',
                                                                validator: (_, fileName) => {
                                                                    if (
                                                                        !fileName ||
                                                                        (fileName.length > 4 &&
                                                                            fileName.endsWith('.zip')) ||
                                                                        (fileName.length > 5 &&
                                                                            (fileName.endsWith('.dsym') ||
                                                                                fileName.endsWith('.dSYM')))
                                                                    ) {
                                                                        return Promise.resolve();
                                                                    } else {
                                                                        return Promise.reject(
                                                                            'dSYM filename must be of type .zip, .dsym, or .dSYM.',
                                                                        );
                                                                    }
                                                                },
                                                            },
                                                        ]}
                                                        name={[listFieldData.name, 'fileName']}
                                                    >
                                                        <Input allowClear style={{ width: '100%' }} />
                                                    </Form.Item>
                                                </div>
                                                <div style={{ marginTop: -10 }}>
                                                    <Text style={{ color: antGrey.grey7 }}>
                                                        Tokenized patterns and wildcards are supported.
                                                    </Text>{' '}
                                                    <Popover
                                                        content={
                                                            <PatternTokenHelperTextComponent
                                                                availableTokens={availableTokens}
                                                            />
                                                        }
                                                    >
                                                        <InfoCircleOutlined style={{ color: antGrey.grey7 }} />
                                                    </Popover>
                                                </div>
                                            </Space>

                                            <Space
                                                direction="vertical"
                                                size={14}
                                                style={{
                                                    width: '100%',
                                                    paddingTop: 8,
                                                }}
                                            >
                                                <Space align="center">
                                                    <Text
                                                        style={{
                                                            fontWeight: 600,
                                                            color: antGrey.grey8,
                                                        }}
                                                    >
                                                        Upload to
                                                    </Text>
                                                    <Tooltip title="Which stability monitoring integrations should receive dSYMs matching this rule. Options come from the stability monitoring integrations connected to this step's downstream rollout step(s).">
                                                        <InfoCircleOutlined style={{ color: antGrey.grey6 }} />
                                                    </Tooltip>
                                                </Space>
                                                <Form.Item
                                                    noStyle
                                                    {...listFieldData}
                                                    name={[listFieldData.name, 'stabilityMonitoringInstallationIds']}
                                                    rules={[
                                                        {
                                                            validator: (_, value) => {
                                                                if (
                                                                    destinationOptions.length > 0 &&
                                                                    (!value || value.length === 0)
                                                                ) {
                                                                    return Promise.reject(
                                                                        'Select at least one stability monitoring integration to upload to.',
                                                                    );
                                                                }
                                                                return Promise.resolve();
                                                            },
                                                        },
                                                    ]}
                                                >
                                                    <Select
                                                        mode="multiple"
                                                        placeholder={
                                                            destinationOptions.length === 0 ?
                                                                'No stability monitoring integrations connected to downstream rollout steps'
                                                            :   'Select stability monitoring integrations'
                                                        }
                                                        disabled={destinationOptions.length === 0}
                                                        style={{ width: '100%' }}
                                                    >
                                                        {destinationOptions.map((option) => {
                                                            const provenance =
                                                                option.rolloutStepNames.length > 0 ?
                                                                    ` (used by ${option.rolloutStepNames.join(', ')})`
                                                                :   '';
                                                            return (
                                                                <Select.Option
                                                                    value={option.installationId}
                                                                    key={option.installationId}
                                                                >
                                                                    {option.integrationName}
                                                                    <Text style={{ color: antGrey.grey7 }}>
                                                                        {provenance}
                                                                    </Text>
                                                                </Select.Option>
                                                            );
                                                        })}
                                                    </Select>
                                                </Form.Item>
                                            </Space>
                                        </Space>
                                    </>
                                );
                            }}
                        </Form.Item>
                    );
                },
            },
        ],
        listChildrenWrapper: (children, { add }) => {
            const buttonText = (children as React.ReactNode[]).length === 0 ? 'Add rule' : 'Add another rule';
            return (
                <Space
                    direction="vertical"
                    style={{
                        width: '100%',
                    }}
                    size={16}
                >
                    {children}
                    <Form.Item shouldUpdate noStyle>
                        {(form) => {
                            const currentUploadSettings = form.getFieldValue('uploadWorkflowSettings');
                            const disableButton = currentUploadSettings?.length > 1;
                            return (
                                <>
                                    <Button
                                        disabled={disableButton}
                                        icon={<PlusOutlined style={{ color: runwayColors.primary }} />}
                                        onClick={() =>
                                            add({
                                                workflowType: null,
                                                fileName: `{*}.dSYM.zip`,
                                                stabilityMonitoringInstallationIds:
                                                    defaultStabilityMonitoringInstallationIds,
                                            })
                                        }
                                    >
                                        {buttonText}
                                    </Button>
                                </>
                            );
                        }}
                    </Form.Item>
                </Space>
            );
        },
    } as FormFieldData;
}
